import { store } from "@store/store";
import type { LoaderFunctionArgs } from "react-router";
import { getPostHog } from "@/analytics/posthog";
import { classifyCompanyBySlugError } from "@/services/companyBySlugError";
import { companyQueryOptions } from "@/services/useCompaniesById";
import { companyBySlugQueryOptions } from "@/services/useGetCompanyBySlug";
import { safeConsole } from "@/utils/safeConsole";
import { queryClient } from "../queryClient";

/**
 * Auth context for route loaders, read straight from the store because loaders
 * run outside React.
 *
 * Deliberately NOT the tenant. The URL owns *what* you are looking at; the
 * session owns *who you are*. `companyId` here is only the legacy fallback for
 * the unprefixed route mount — see `resolveTenantCompanyId`.
 */
const getAuthContext = () => {
  const state = store.getState();
  return {
    accessToken: state.session.accessToken,
    accountId: state.session?.user?.accountId,
    sessionCompanyId: state.session?.companyId ?? "",
    role: state.session?.user?.role,
  };
};

export type CompanyDataLoaderDependencies = {
  getAuthContext: typeof getAuthContext;
  loadCompanyBySlug: (
    slug: string,
    accessToken: string,
    accountId: string,
  ) => Promise<{ _id?: string } | undefined>;
  prefetchCompany: (companyId: string, accessToken: string) => Promise<void>;
  classifySlugError: typeof classifyCompanyBySlugError;
  reportPrefetchError: (error: Error) => void;
};

const companyDataLoaderDependencies: CompanyDataLoaderDependencies = {
  getAuthContext,
  loadCompanyBySlug: (slug, accessToken, accountId) =>
    queryClient.ensureQueryData(
      companyBySlugQueryOptions(slug, accessToken, accountId),
    ),
  prefetchCompany: async (companyId, accessToken) => {
    await queryClient.ensureQueryData(
      companyQueryOptions(companyId, accessToken),
    );
  },
  classifySlugError: classifyCompanyBySlugError,
  reportPrefetchError: (error) => {
    safeConsole.error(
      "route-loading",
      "companyDataLoader prefetch failed:",
      error,
    );
    getPostHog()?.captureException(error);
  },
};

/**
 * Resolves which tenant this navigation is for.
 *
 * Prefers the URL slug (SD-12672). React Router runs parent and child loaders
 * in **parallel**, and the slug -> Redux commit happens later still, in
 * `CompanySlugTenantCommitter`'s layout effect after the shell renders. So
 * during an `alpha -> beta` navigation the store answers "alpha" while the URL
 * already says "beta", and a loader reading the store prefetches the previous
 * tenant. Tenant-prefixed query keys kept that mis-fetch out of the new
 * tenant's cache entries, but nothing made the sequence correct — hence the
 * hand-written mismatch guards that had accumulated around this seam.
 *
 * Nested loaders receive the parent segment's params, so `params.slug` is the
 * tenant this navigation is actually for, available before anything commits.
 * The slug lookup reuses `companySlugLoader`'s exact query key, so TanStack
 * Query dedupes it against that in-flight request rather than issuing a second
 * one or forcing a waterfall.
 *
 * Falls back to the session for the unprefixed legacy mount, which has no
 * `:slug` segment and still takes its company from localStorage. That mount is
 * slated for removal (SD-11897/SD-12120); this fallback goes with it.
 */
const resolveTenantCompanyId = async (
  slug: string | undefined,
  accessToken: string,
  accountId: string | undefined,
  sessionCompanyId: string,
  dependencies: CompanyDataLoaderDependencies,
): Promise<string | null> => {
  if (!slug || !accountId) return sessionCompanyId;

  try {
    const company = await dependencies.loadCompanyBySlug(
      slug,
      accessToken,
      accountId,
    );
    return company?._id ?? sessionCompanyId;
  } catch (error) {
    // 404 (no such slug) and 403 (slug exists, caller can't reach it) are the
    // resolver's normal access-gate verdicts, not faults: `companySlugLoader`
    // classifies them the same way and quietly bounces to the company picker.
    // Reporting them from this prefetch would fill monitoring with exceptions
    // for a case the app already handles, and devalue the signal the catch
    // below exists to preserve. Decline to prefetch and let the parent redirect.
    const kind = dependencies.classifySlugError(error);
    if (kind === "not-found" || kind === "forbidden") return null;
    throw error;
  }
};

/**
 * Shared loader for routes whose components need company data on first
 * paint (/campaigns, /coach, /booking-wizard). Prefetches company data so
 * those components don't waterfall.
 * Returns null — data flows through the React Query cache, not loaderData.
 *
 * This is a PREFETCH, never a gate: a failure must not become a route crash.
 * - Restricted tenant roles are denied the generic company
 *   object (backend 403s the company gate), so we skip the prefetch entirely
 *   rather than fire a request we know will fail. Their pages fetch their own
 *   permission-scoped data.
 * - Any other failure is swallowed: the route still renders, and components
 *   re-fetch via `useCompaniesById` and surface their own error/empty state. A
 *   doomed prefetch must never surface as the RouteErrorBoundary "Uh-Oh" page.
 * - An unknown or forbidden slug is an expected verdict, not a fault:
 *   `companySlugLoader` owns bouncing the user to the picker, so this loader
 *   declines to prefetch *without* reporting. Only genuine failures reach the
 *   catch, which keeps that signal worth watching.
 */
export const createCompanyDataLoader =
  (dependencies: CompanyDataLoaderDependencies) =>
  async ({ params }: Pick<LoaderFunctionArgs, "params">) => {
    const { accessToken, accountId, sessionCompanyId, role } =
      dependencies.getAuthContext();
    if (
      !accessToken ||
      role === "coach_csr" ||
      role === "rep" ||
      role === "dispatcher" ||
      role === "technician"
    ) {
      return null;
    }

    try {
      const companyId = await resolveTenantCompanyId(
        params.slug,
        accessToken,
        accountId,
        sessionCompanyId,
        dependencies,
      );
      if (!companyId) return null;

      await dependencies.prefetchCompany(companyId, accessToken);
    } catch (error) {
      dependencies.reportPrefetchError(
        error instanceof Error ? error : new Error("Company prefetch failed"),
      );
    }

    return null;
  };

export const companyDataLoader = createCompanyDataLoader(
  companyDataLoaderDependencies,
);
